import { isServerMode } from '../platform/server-mode';
/* Каркас вызывает BotSaveChatVoice перед закрытием. Мост сохраняет голос через backend,
   затем закрывает Telegram Mini App; при ошибке восстанавливает последний подтверждённый выбор.
   Быстрые повторные выборы сохраняются последовательно. Прототип работает локально без API. */
import { ApiError } from '../api/client';
import { endpoints } from '../api';
import type { TtsEngine } from '../api/types';
import { platform } from '../platform';
import { getPicker } from '../features/tts/runtime';
import { readShellMemory } from './shell-memory';

const SAY_MS = 2400;
/** Сколько видна плашка «Голос выбран» до закрытия окна (вход за голосом для переозвучки) */
const PICKED_MS = 1800;

interface ShellGlobals {
  BotChatTts?: { close(): void; setVoice(id: number | null): void };
  /** engine — модель синтеза из карточки голоса (STEOS v2 / v1); короткое нажатие в списке — v2 */
  BotSaveChatVoice?: (id: number, engine?: TtsEngine) => void;
}

function storedVoice(): number | null {
  const v = readShellMemory()?.tts?.voice;
  return typeof v === 'number' ? v : null;
}

function sayAtRow(id: number, text: string) {
  document.querySelectorAll<HTMLElement>(`[data-voice="${id}"] .m span`).forEach(s => {
    const was = s.textContent;
    s.textContent = text;
    s.style.color = 'var(--bad, #ef4444)';
    setTimeout(() => {
      s.textContent = was;
      s.style.color = '';
    }, SAY_MS);
  });
}

let installed = false;

export function installTtsVoiceBridge() {
  const w = window as unknown as ShellGlobals;
  if (installed || !w.BotChatTts) return;
  installed = true;
  const shell = w.BotChatTts;
  /** Голос, подтверждённый сервером: к нему откат. До первого выбора — тот, что был в памяти страницы (сессия не запрашивается заранее) */
  let confirmed: number | null = storedVoice();
  let saving = false;
  let wanted: number | null = null;
  let wantedEngine: TtsEngine = 'v2';

  w.BotSaveChatVoice = function (id: number, engine: TtsEngine = 'v2') {
    const serverMode = isServerMode();
    if (!serverMode) return shell.close();
    wanted = id;
    wantedEngine = engine;
    if (saving) return;
    saving = true;
    void (async () => {
      try {
        while (wanted != null) {
          const next: number = wanted;
          const nextEngine = wantedEngine;
          const result = await endpoints.setTtsVoice(next, nextEngine);
          confirmed = result.voice_id;
          if (wanted === next && wantedEngine === nextEngine) break;
        }
        /* вход из бота за голосом для переозвучки (go_tts_s2s): плашка «Голос выбран», окно закрывается чуть позже — её успевают прочитать */
        const picker = getPicker();
        if (picker?.state.only === 's2s' && picker.state.note) {
          picker.state.note = { kind: 'ok', text: 'Голос выбран — вернитесь в чат и нажмите «Продолжить переозвучку»' };
          picker.render();
          platform.haptic('success');
          setTimeout(() => platform.close(), PICKED_MS);
        } else platform.close();
      } catch (e: unknown) {
        platform.haptic('error');
        shell.setVoice(confirmed);
        sayAtRow(id, e instanceof ApiError ? e.message : 'Не получилось выбрать голос');
      } finally {
        saving = false;
        wanted = null;
      }
    })();
  };
}
